import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Button from "../../components/Button";
import { fetchPortfolioProjects } from '../../lib/projects'

const CATEGORY_CONFIG = {
  MVPS: {
    name: "MVPs",
    color: "from-[#A2BC6B] to-[#8da65b]",
    bgColor: "bg-[#A2BC6B]/10",
    borderColor: "border-[#A2BC6B]/30",
    textColor: "text-neutral-400",
    icon: "🚀"
  },
  OPEN_SOURCE: {
    name: "Open Source",
    color: "from-[#A2BC6B] to-[#8da65b]",
    bgColor: "bg-[#A2BC6B]/10",
    borderColor: "border-[#A2BC6B]/30",
    textColor: "text-[#A2BC6B]",
    icon: "🔓"
  },
  DESIGN_SYSTEM: {
    name: "Design System",
    color: "from-[#A2BC6B] to-[#8da65b]",
    bgColor: "bg-[#A2BC6B]/10",
    borderColor: "border-[#A2BC6B]/30",
    textColor: "text-[#A2BC6B]",
    icon: "🎨"
  },
  WEB_APPS: {
    name: "Web Apps",
    color: "from-[#A2BC6B] to-[#8da65b]",
    bgColor: "bg-[#A2BC6B]/10",
    borderColor: "border-[#A2BC6B]/30",
    textColor: "text-[#A2BC6B]",
    icon: "💻"
  },
  WEBSITES: {
    name: "Websites",
    color: "from-[#A2BC6B] to-[#8da65b]",
    bgColor: "bg-[#A2BC6B]/10",
    borderColor: "border-[#A2BC6B]/30",
    textColor: "text-[#A2BC6B]",
    icon: "🌐"
  },
  EXPERIMENTS: {
    name: "Experiments",
    color: "from-[#A2BC6B] to-[#8da65b]",
    bgColor: "bg-[#A2BC6B]/10",
    borderColor: "border-[#A2BC6B]/30",
    textColor: "text-[#A2BC6B]",
    icon: "🧪"
  },
  IMPROVEMENTS: {
    name: "Improvements",
    color: "from-[#A2BC6B] to-[#8da65b]",
    bgColor: "bg-[#A2BC6B]/10",
    borderColor: "border-[#A2BC6B]/30",
    textColor: "text-[#A2BC6B]",
    icon: "📈"
  }
};

const ProjectLink = ({ project }) => {
  const isExternal =
    project.isExternalProjectLink || project.link?.startsWith("http");

  if (isExternal) {
    return (
      <a href={project.link} target="_blank" rel="noopener noreferrer" className="inline-block">
        <Button text="View" variant="neutral" />
      </a>
    );
  }

  const destination =
    project.link && project.link !== "#"
      ? project.link
      : project.slug
        ? `/project/${project.slug}`
        : null;

  return destination ? (
    <Link to={destination} className="inline-block">
      <Button text="View" variant="neutral" />
    </Link>
  ) : (
    <span className="text-sm text-neutral-500">Case study coming soon</span>
  );
};

const ProjectSection = () => {
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [hoveredFolder, setHoveredFolder] = useState(null)

  const projectCategories = useMemo(() => {
    const order = ['WEB_APPS', 'IMPROVEMENTS', 'DESIGN_SYSTEM', 'MVPS', 'OPEN_SOURCE', 'EXPERIMENTS'];
    return order.map(key => {
      const config = CATEGORY_CONFIG[key];
      // Filter projects that have this category
      const categoryProjects = projects.filter(p => {
        if (!p.category) return false;
        if (Array.isArray(p.category)) return p.category.includes(key);
        return p.category === key;
      });
      
      if (categoryProjects.length === 0) return null;

      return {
        ...config,
        projects: categoryProjects.map(p => ({
          ...p,
          type: config.name // Use category name as type for display
        }))
      };
    }).filter(Boolean);
  }, [projects]);

  useEffect(() => {
    let mounted = true
    async function load() {
      try {
        const items = await fetchPortfolioProjects({ limit: 20 })

        if (mounted && Array.isArray(items) && items.length > 0) {
          // map items to the shape expected by the component (image could be URL)
          const mapped = items.map(it => ({
            ...it,
            image: it.image || undefined,
            title: it.title || 'Untitled',
            details: it.details || '',
            link: it.link || '#',
          }))
          setProjects(mapped)
          setLoading(false)
          console.log(mapped);
        }
      } catch (err) {
        console.error(err.message || String(err))
      }
    }

    load()
    return () => { mounted = false }
  }, [])

  if (loading) {
    return (
      <section id="work" className="py-10 px-5 md:px-10 pt-0 scroll-mt-24">
        <div className="flex items-center justify-center h-48" role="status" aria-live="polite">
          <svg className="w-8 h-8 text-neutral-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
          </svg>
          <span className="ml-3 text-neutral-500">Loading projects...</span>
        </div>
      </section>
    )
  }

  return (
    <section id="work" className="py-10 px-5 md:px-10 pt-0 scroll-mt-24">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6">
          <div>
            <p className="uppercase text-base font-medium text-neutral-500">Selected work</p>
            <h2 className="uppercase font-medium text-2xl mt-2">Design judgment, shipped in code</h2>
          </div>
        </div>

        {/* Category Filter Pills */}
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={() => setHoveredFolder(null)}
            className={`flex items-center gap-2 px-6 py-2 border rounded-full cursor-pointer transition-all duration-300 ${hoveredFolder === null ? 'bg-neutral-100 text-black border-transparent scale-105 font-medium' : 'bg-neutral-900 text-neutral-400 border-neutral-800 hover:bg-neutral-800'}`}
          >
            All Work
          </button>
          
          {projectCategories.map((category, index) => (
            <button
              key={index}
              onClick={() => setHoveredFolder(index)}
              className={`flex items-center gap-2 px-3 py-2 border rounded-full cursor-pointer transition-all duration-300 ${hoveredFolder === index ? 'bg-[#A2BC6B] text-black border-transparent scale-105 shadow-lg' : 'bg-neutral-900 text-neutral-400 border-neutral-800 hover:bg-neutral-800 hover:scale-102'}`}
            >
              <span className="text-sm">{category.icon}</span>
              <span className={`text-sm font-medium ${hoveredFolder === index ? 'text-black' : 'text-neutral-400'}`}>
                {category.name}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${hoveredFolder === index ? 'bg-black/10 text-black' : 'bg-neutral-800 text-neutral-500'}`}>
                {category.projects.length}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Content Area */}
      <div className="min-h-[500px]">
        {hoveredFolder === null ? (
          /* All Work Grid View */
          <div className="animate-fadeIn">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project, index) => (
                <div
                  key={index}
                  className="group relative bg-neutral-900 border border-neutral-800 rounded-xl p-5 flex flex-col h-full"
                  style={{
                    animation: `fadeInUp 0.4s ease-out ${index * 0.1}s both`
                  }}
                >
                  <div className="w-full h-48 mb-4 rounded-lg overflow-hidden bg-neutral-800">
                    <img
                      src={project.image || 'https://via.placeholder.com/800x600?text=No+Image'}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/800x600?text=Image+Not+Found';
                      }}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      alt={project.title}
                    />
                  </div>
                  
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h4 className="font-semibold text-neutral-200 mb-2 group-hover:text-white transition-colors line-clamp-2">
                        {project.title}
                      </h4>
                      <p className="text-sm text-neutral-400 line-clamp-2 mb-4">
                        {project.details}
                      </p>
                    </div>
                    
                    <div className="mt-auto">
                      <ProjectLink project={project} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Category Grid View */
          <div className="animate-fadeIn">
            {/* <div className={`mb-6 p-6 rounded-xl border ${projectCategories[hoveredFolder].borderColor} ${projectCategories[hoveredFolder].bgColor}`}>
              <div className="flex items-center gap-4">
                <span className="text-4xl">{projectCategories[hoveredFolder].icon}</span>
                <div>
                  <h3 className={`text-2xl font-bold ${projectCategories[hoveredFolder].textColor}`}>
                    {projectCategories[hoveredFolder].name}
                  </h3>
                  <p className="text-sm text-neutral-400 mt-1">
                    {projectCategories[hoveredFolder].projects.length} projects available
                  </p>
                </div>
              </div>
            </div> */}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projectCategories[hoveredFolder].projects.map((project, projectIndex) => (
                <div
                  key={projectIndex}
                  className={`group relative bg-neutral-900 border ${projectCategories[hoveredFolder].borderColor} rounded-xl p-5 flex flex-col h-full`}
                  style={{
                    animation: `fadeInUp 0.4s ease-out ${projectIndex * 0.1}s both`
                  }}
                >
                  <div className="w-full h-48 mb-4 rounded-lg overflow-hidden bg-neutral-800">
                    {project.image ? (
                      <img
                        src={project.image}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        alt={project.title}
                      />
                    ) : null}
                    {/* Fallback to icon if no image or error */}
                    <div 
                      className={`w-full h-full bg-gradient-to-br ${projectCategories[hoveredFolder].color} opacity-20 group-hover:opacity-30 transition-opacity flex items-center justify-center ${project.image ? 'hidden' : 'flex'}`}
                    >
                      <span className="text-5xl opacity-50">{projectCategories[hoveredFolder].icon}</span>
                    </div>
                  </div>
                  
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h4 className="font-semibold text-neutral-200 mb-2 group-hover:text-white transition-colors">
                        {project.title}
                      </h4>
                      <div className={`text-sm ${projectCategories[hoveredFolder].textColor} flex items-center gap-1.5 mb-4`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                        {project.type}
                      </div>
                    </div>
                    
                    <div className="mt-auto">
                      <ProjectLink project={project} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProjectSection;
